import { __awaiter } from "tslib";
import { AppLinkDomains } from '@office-start/app-host-types';
import { AppbarConnectorService } from '@office-start/appbar-connector-service';
import { fetchOfficeComponentContext } from '@office-start/component-context-service';
import { launchMetaOSApp } from '@office-start/launcher-service';
import { fetchLocStrings } from '@office-start/loc-service';
import { PermissionStatus, } from '@office-start/metaos-hub-sdk';
import { logFeature } from '@office-start/telemetry-service';
import { logAppHostError } from '../utils/logAppHostError';
import { logMosHubFeature } from '../utils/logMosHubFeature';
import { getAuthenticationService, getNestedAppAuthenticationService, } from './AppHostAuthenticationService';
import { shouldEnableNestedAppAuth } from './AppHostAuthenticationService/shouldEnableNestedAppAuth';
import { AppHostTelemetryService } from './AppHostTelemetryService';
import { createHubServices } from './createHubServices';
import { isCopilotAppCommandServiceEnabled } from './ExtensibilityProviders/isCopilotAppCommandServiceEnabled';
import { isCopilotExtensibilityEnabled } from './ExtensibilityProviders/isCopilotExtensibilityEnabled';
export const getHubServices = ({ appId, appHostSessionId, frameContext, appbarConnectorService, dialogHandlers, retryCallback, setFrameInfoCallback, setShareLinkCallback, showConsentDialogCallback, userId, appInfo, userInfo, defaultLocStrings, isStandaloneCopilotApp, featureGates, }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const cachingEnabled = (_a = featureGates === null || featureGates === void 0 ? void 0 : featureGates.cachingGates) === null || _a === void 0 ? void 0 : _a.cachingEnabled;
    const telemetryService = new AppHostTelemetryService(appHostSessionId, frameContext, appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged, (_b = window.location.pathname) !== null && _b !== void 0 ? _b : appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.appUrl, isStandaloneCopilotApp);
    let authenticationService, nestedAppAuthenticationService, officeComponentContext, locStrings;
    const logHubServicesMetrics = (startTimer, scenarioName) => {
        var _a, _b;
        logMosHubFeature({
            appHostSessionId,
            appId,
            clientTimestamp: startTimer,
            error: '',
            frameContext,
            messageDelayMs: Date.now() - startTimer,
            scenarioName,
            isTileChange: (_a = appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.tileChanged) !== null && _a !== void 0 ? _a : false,
            appUrl: (_b = window.location.pathname) !== null && _b !== void 0 ? _b : appbarConnectorService === null || appbarConnectorService === void 0 ? void 0 : appbarConnectorService.appUrl,
        });
    };
    const fetchService = (serviceFn, scenarioName) => __awaiter(void 0, void 0, void 0, function* () {
        const startTimer = Date.now();
        const result = yield serviceFn();
        logHubServicesMetrics(startTimer, scenarioName);
        return result;
    });
    const defaultServices = [
        () => fetchService(() => getAuthenticationService(appId), 'GetAuthenticationService'),
        () => fetchService(getNestedAppAuthenticationService, 'GetNestedAppAuthenticationService'),
        () => fetchService(fetchOfficeComponentContext, 'FetchOfficeComponentContext'),
    ];
    const additionalService = () => fetchService(fetchLocStrings, 'FetchLocStrings');
    if (defaultLocStrings) {
        const results = yield Promise.all(defaultServices.map((fn) => fn()));
        [
            authenticationService,
            nestedAppAuthenticationService,
            officeComponentContext,
        ] = results;
        locStrings = defaultLocStrings;
    }
    else {
        const results = yield Promise.all([...defaultServices, additionalService].map((fn) => fn()));
        [
            authenticationService,
            nestedAppAuthenticationService,
            officeComponentContext,
            locStrings,
        ] = results;
    }
    if (!officeComponentContext ||
        !authenticationService ||
        !locStrings ||
        !nestedAppAuthenticationService) {
        return undefined;
    }
    const appLinkDomains = AppLinkDomains;
    const noOpFunction = () => undefined;
    const isNestedAppAuthServiceEnabled = shouldEnableNestedAppAuth(officeComponentContext, featureGates);
    return yield createHubServices({
        appId,
        appLinkDomains,
        appLinkRootPath: "m365apps" /* RoutePaths.M365Apps */,
        appbarConnectorService: appbarConnectorService !== null && appbarConnectorService !== void 0 ? appbarConnectorService : AppbarConnectorService.getInstance(),
        authenticationService,
        nestedAppAuthenticationService,
        capabilitiesFeatureGates: {
            deepLinkServiceEnabled: true,
            dialogServiceEnabled: true,
            nestedAppAuthServiceEnabled: isNestedAppAuthServiceEnabled,
            copilotExtensibilityEnabled: isCopilotExtensibilityEnabled(appId, featureGates),
            enableAppCommandService: isCopilotAppCommandServiceEnabled(appId, featureGates),
            cachingEnabled: cachingEnabled !== null && cachingEnabled !== void 0 ? cachingEnabled : false,
        },
        dialogHandlers,
        locStrings: locStrings.metaOSHubResources,
        navigateForwardCallback: () => {
            var _a, _b;
            logFeature({
                featureName: "MOSHub" /* KnownFeatureNames.MOSHub */,
                eventName: 'ReturnFocus',
                params: {
                    AppId: appId,
                    AppCapabilities: 'NavigateForward',
                    DebugInfo: `focus set to search box`,
                    Service: 'NavigateForwardPageService',
                },
                area: 'MOSAppHost',
                subArea: 'AppHostPageService',
            });
            (_b = (_a = window.O365Shell) === null || _a === void 0 ? void 0 : _a.Search) === null || _b === void 0 ? void 0 : _b.FocusSearchBox();
        },
        navigateToAppCallback: (params) => {
            const appDetails = {
                appId: params.appId,
                entityId: params.pageId,
                subPageId: params.subPageId ? params.subPageId : '',
            };
            const area = 'MOSAppHost';
            logFeature({
                featureName: "MOSHub" /* KnownFeatureNames.MOSHub */,
                eventName: 'NavigateToApp',
                params: {
                    AppId: params.appId,
                    AppCapabilities: 'NavigateToApp',
                    DebugInfo: 'NavigateToApp',
                    Service: 'NavigateToAppPageService',
                },
                area: 'MOSAppHost',
                subArea: 'AppHostPageService',
            });
            launchMetaOSApp(appDetails, area).then((result) => {
                if (!result.success) {
                    logAppHostError('Navigate_App_Service_Error', result.message);
                }
            });
        },
        officeComponentContext,
        retryCallback: retryCallback !== null && retryCallback !== void 0 ? retryCallback : noOpFunction,
        setFrameInfoCallback: setFrameInfoCallback !== null && setFrameInfoCallback !== void 0 ? setFrameInfoCallback : noOpFunction,
        setShareLinkCallback: setShareLinkCallback !== null && setShareLinkCallback !== void 0 ? setShareLinkCallback : noOpFunction,
        telemetryService,
        showConsentDialogCallback: showConsentDialogCallback !== null && showConsentDialogCallback !== void 0 ? showConsentDialogCallback : ((_) => {
            console.log('NoOp consent callback');
            return Promise.resolve(PermissionStatus.NeverAsked);
        }),
        userId,
        appInfo,
        userInfo,
    });
});
//# sourceMappingURL=getHubServices.js.map